/** @format */

import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	OnInit,
	PLATFORM_ID,
	signal
} from '@angular/core';
import {
	HomefieldIconLibraryModule,
	homefieldIconOrderCartStandard,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { Store } from '@ngrx/store';
import { selectKillswitch, selectResolvedSku } from '@pdp/data-access/state';
import {
	CertonaSchemeItem,
	CertonaService,
	Chain,
	ProcessedNonBundleProduct,
	ProductPrice,
	ProductSku,
	ProductSkuCatalogDetails
} from '@product-recommendations/data-access';
import { ProductService } from '@shared/data-access/product';
import { FavoritesIconComponent } from '@shared/favorites/components';
import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { AuthenticationService } from '@shared/authentication/data-access';
import { CurrencyPipe, ImageLoaderConfig, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { WishListService } from '@shared/favorites/data-access/services';
import {
	HmfActionTextComponent,
	HmfBadgeComponent,
	HmfButtonComponent
} from '@dsg-tech/homefield-angular';
import { FBTProduct } from '@pdp/data-access/interfaces';
import { OptimizedImageComponentModule } from '@shared/media/components';
import { getProductPriceFromSku, getProductPriceFromStyle } from '@pdp/utils';
import { ATC_BUTTON_TEXTS } from '@pdp/add-to-cart/data-access';
import { BreakpointsService, Logger } from '@shared/data-access/services';
import { BreakPointNew } from '@shared/data-access/enums';

@Component({
	selector: 'pdp-frequently-bought-together',
	imports: [
		HomefieldIconLibraryModule,
		FavoritesIconComponent,
		HmfBadgeComponent,
		OptimizedImageComponentModule,
		CurrencyPipe,
		HmfButtonComponent,
		HmfActionTextComponent
	],
	templateUrl: './frequently-bought-together.component.html',
	styleUrl: './frequently-bought-together.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrequentlyBoughtTogetherComponent implements OnInit {
	private readonly store = inject(Store);
	private readonly certonaService = inject(CertonaService);
	private readonly productService = inject(ProductService);
	private readonly authService = inject(AuthenticationService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly wishlistService = inject(WishListService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly chain = inject(Chain);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly logger = inject(Logger);

	readonly product = input.required<ProcessedNonBundleProduct>();
	readonly selectedColorAttribute = input('');

	readonly resolvedSku = this.store.selectSignal(selectResolvedSku);
	readonly recommendedProductsScheme = this.certonaService.getScheme('product3_rr');
	readonly recommendedProducts = computed(() => this.recommendedProductsScheme()?.items || []);

	readonly recommendedProductDetails = signal<FBTProduct[]>([]);

	readonly pdpSku = computed(() => {
		if (this.resolvedSku()) {
			return this.resolvedSku();
		}

		// If SKU isn't resolved yet, find the SKU that matches the selected color. If there is none, use the first SKU
		const colorMatchedSku = this.product().skus.find((sku) => {
			const skuColor = sku.definingAttributes?.find(
				(attr) => attr.name.toLowerCase() === 'color'
			);
			if (
				this.sanitizeColor(skuColor?.value) ===
				this.sanitizeColor(this.selectedColorAttribute())
			) {
				return sku;
			}
		});

		return colorMatchedSku || this.product().skus[0];
	});
	readonly pdpProductPrice = computed<ProductPrice>(() => {
		if (this.resolvedSku()) {
			return getProductPriceFromSku(this.resolvedSku().prices);
		}
		return getProductPriceFromStyle(this.product().prices);
	});
	readonly pdpProduct = computed<FBTProduct>(() => ({
		skuDetails: this.pdpSku(),
		imageLoaderConfig: this.buildImageConfig(this.pdpSku()),
		prices: this.pdpProductPrice()
	}));

	readonly products = computed<FBTProduct[]>(() => [
		this.pdpProduct(),
		...this.recommendedProductDetails()
	]);

	readonly unavailableImageSkus = signal<string[]>([]);
	readonly unavailableImageSkusSet = computed(() => new Set(this.unavailableImageSkus()));
	readonly unavailableImageConfig: ImageLoaderConfig = {
		src: 'ImageUnavailablePDPSwatch',
		width: 32
	} as const;

	readonly pdpProductColor = computed(
		() =>
			this.pdpSku().definingAttributes?.find((attr) => attr.name.toLowerCase() === 'color')
				?.value
	);
	readonly attributeDisplayTexts = computed(() =>
		this.products().map((product) =>
			product.skuDetails.definingAttributes?.map((attr) => attr.value).join(' | ')
		)
	);
	readonly attributeButtonSize = computed(() =>
		this.breakpointsService.breakpoint() >= BreakPointNew.MEDIUM ? 'medium' : 'small'
	);

	readonly isFBTFavoriteButtonEnabled = this.store.selectSignal(
		selectKillswitch('isFBTFavoriteButtonEnabled')
	);
	readonly isAuthenticated = signal<boolean>(false);
	readonly favoritedECodes = signal<string[]>([]);

	isPL: boolean;

	protected readonly FavoritesLocationEnum = FavoritesLocationEnum;
	protected readonly atcButtonTexts = ATC_BUTTON_TEXTS;

	currentProductServiceCall: Subscription;

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconOrderCartStandard]);

		effect(() => {
			// Once we receive product recs, get their full SKU details
			const recommendedProducts = this.recommendedProducts();
			if (recommendedProducts.length) {
				this.getProductCatalogDetailsForRecs(recommendedProducts.slice(0, 2));
			}
		});
	}

	ngOnInit() {
		this.isPL = this.chain.chainIdentifierAbbr === 'pl';
		if (isPlatformBrowser(this.platformId)) {
			if (this.isFBTFavoriteButtonEnabled()) {
				this.getIsAuthenticatedAndEcodes();
			}
		}
	}

	private getProductCatalogDetailsForRecs(recommendedProducts: CertonaSchemeItem[]) {
		const skus = recommendedProducts.map((item) => item.SKUID);
		// Prevent concurrent requests that can cause race conditions
		if (this.currentProductServiceCall && !this.currentProductServiceCall.closed) {
			this.currentProductServiceCall.unsubscribe();
		}
		this.currentProductServiceCall = this.productService.getProductsBySkus$(skus).subscribe({
			next: (products) => {
				const fbtProducts: FBTProduct[] = products?.map((product) => {
					const recsItem = recommendedProducts.find(
						(recProduct) => recProduct.SKUID === product.partNumber
					);
					return {
						skuDetails: product,
						recsItem,
						imageLoaderConfig: this.buildImageConfig(product),
						prices: getProductPriceFromSku(product.prices)
					};
				});
				if (fbtProducts) {
					this.recommendedProductDetails.set(fbtProducts);
				}
			},
			error: (err) => {
				this.logger.error('Failed to get product details for recommended products:', err);
			}
		});
	}

	private getIsAuthenticatedAndEcodes(): void {
		this.authService
			.getIsAuthenticated$()
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				concatMap((isAuthed: boolean) => {
					this.isAuthenticated.set(isAuthed);
					// Only call to get favorited Ecodes if authenticated
					return isAuthed ? this.getFavoritedECodes() : of([]);
				}),
				catchError(() => {
					return of([]);
				})
			)
			.subscribe();
	}

	private getFavoritedECodes(): Observable<string[]> {
		return this.wishlistService.getFavoritedEcodes().pipe(
			map((savedProducts: { [key: string]: string[] }) => Object.keys(savedProducts || {})),
			tap((ecodes) => this.favoritedECodes.set(ecodes))
		);
	}

	private sanitizeColor(color: string) {
		if (!color) {
			return color;
		}
		return color.replace(/[/ -]/g, '_');
	}

	private buildImageConfig(sku: ProductSku | ProductSkuCatalogDetails): ImageLoaderConfig {
		const colorAttr = sku.definingAttributes?.find(
			(attr) => attr.name.toLowerCase() === 'color'
		);
		return {
			src: sku.parentPartNumber,
			width: 264,
			loaderParams: {
				swatchColor: colorAttr?.value,
				fit: 'constrain'
			}
		};
	}

	openQuickviewPanel(product: FBTProduct): void {
		const ecode = product.skuDetails.parentPartNumber;
		const sku = product.skuDetails.partNumber;

		const productQuickViewParams = {
			skuPartNumber: sku,
			additionalData: {}
		};

		if (product.recsItem?.URL) {
			// capture recid from url
			const recidMatch = /recid=([^&]+)/.exec(product.recsItem.URL);
			productQuickViewParams.additionalData = recidMatch ? recidMatch[1] : '';
		}

		window.quickview.openProductQuickView(ecode, 'certona', productQuickViewParams);
	}

	handleImageError(sku: string) {
		this.unavailableImageSkus.update((skus) => [...skus, sku]);
	}
}
